/**
 * Apply-loan only persistence (does not change apply-now behavior).
 * Shared form keys (loanFormData, aadhaarData, mobileNumber) stay the same
 * so existing step components keep working; we add step resume + durable copies.
 */
import {
  clearApplyFlowOnSuccess,
  getAadhaarNumberSecure,
  getAadhaarReferenceSecure,
} from "./applyFlowStorage";

const APPLY_LOAN_STEP_KEY = "applyLoanStep";
const APPLY_LOAN_AADHAAR_NUMBER_KEY = "applyLoanAadhaarNumber";
const APPLY_LOAN_AADHAAR_REF_KEY = "applyLoanAadhaarRef";
const APPLY_LOAN_FLAG_KEY = "applyLoanActive";
const APPLY_LOAN_MOBILE_OTP_DRAFT_KEY = "applyLoanMobileOtpDraft";
const APPLY_LOAN_AADHAAR_OTP_DRAFT_KEY = "applyLoanAadhaarOtpDraft";
const APPLY_LOAN_DIGITAP_DRAFT_KEY = "fundswalla_apply_loan_draft";
const APPLY_LOAN_DIGITAP_TXN_KEY = "fundswalla_digitap_pending_txn";
const APPLY_LOAN_DIGITAP_CALLBACK_TXN_KEY = "fundswalla_digitap_callback_txn";

const APPLY_LOAN_ONLY_KEYS = [
  APPLY_LOAN_STEP_KEY,
  APPLY_LOAN_AADHAAR_NUMBER_KEY,
  APPLY_LOAN_AADHAAR_REF_KEY,
  APPLY_LOAN_FLAG_KEY,
  APPLY_LOAN_MOBILE_OTP_DRAFT_KEY,
  APPLY_LOAN_AADHAAR_OTP_DRAFT_KEY,
  APPLY_LOAN_DIGITAP_DRAFT_KEY,
  APPLY_LOAN_DIGITAP_TXN_KEY,
  APPLY_LOAN_DIGITAP_CALLBACK_TXN_KEY,
];

const MOBILE_OTP_RESEND_SECONDS = 30;
const AADHAAR_OTP_RESEND_SECONDS = 60;

/** Persist "OTP already sent" so refresh shows timer + OTP field, not Get OTP */
export const saveApplyLoanMobileOtpDraft = ({ mobile, sentAt }) => {
  const clean = String(mobile || "").replace(/\D/g, "").slice(-10);
  if (clean.length !== 10) return;
  localStorage.setItem(
    APPLY_LOAN_MOBILE_OTP_DRAFT_KEY,
    JSON.stringify({
      mobile: clean,
      sentAt: Number(sentAt) || Date.now(),
      consent: true,
    })
  );
};

export const getApplyLoanMobileOtpDraft = () => {
  try {
    const raw = localStorage.getItem(APPLY_LOAN_MOBILE_OTP_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const mobile = String(parsed.mobile || "").replace(/\D/g, "").slice(-10);
    const sentAt = Number(parsed.sentAt) || 0;
    if (mobile.length !== 10 || !sentAt) return null;
    const elapsedSec = Math.floor((Date.now() - sentAt) / 1000);
    const retryTimer = Math.max(0, MOBILE_OTP_RESEND_SECONDS - elapsedSec);
    return {
      mobile,
      sentAt,
      consent: parsed.consent !== false,
      retryTimer,
      otpSent: true,
    };
  } catch {
    return null;
  }
};

export const clearApplyLoanMobileOtpDraft = () => {
  localStorage.removeItem(APPLY_LOAN_MOBILE_OTP_DRAFT_KEY);
};

export const APPLY_LOAN_MOBILE_OTP_RESEND_SECONDS = MOBILE_OTP_RESEND_SECONDS;
export const APPLY_LOAN_AADHAAR_OTP_RESEND_SECONDS = AADHAAR_OTP_RESEND_SECONDS;

/** Persist Aadhaar OTP-sent state so refresh keeps OTP UI + timer + API message */
export const saveApplyLoanAadhaarOtpDraft = ({
  aadhaar,
  referenceId,
  sentAt,
  retrySeconds,
  apiMessage,
  responseCode,
}) => {
  const clean = String(aadhaar || "").replace(/\D/g, "").slice(0, 12);
  if (clean.length !== 12) return;
  const seconds = Math.max(
    1,
    Number(retrySeconds) || AADHAAR_OTP_RESEND_SECONDS
  );
  localStorage.setItem(
    APPLY_LOAN_AADHAAR_OTP_DRAFT_KEY,
    JSON.stringify({
      aadhaar: clean,
      referenceId: String(referenceId || ""),
      sentAt: Number(sentAt) || Date.now(),
      retrySeconds: seconds,
      apiMessage: String(apiMessage || ""),
      responseCode: String(responseCode || ""),
      consent: true,
    })
  );
  localStorage.setItem(APPLY_LOAN_AADHAAR_NUMBER_KEY, clean);
  if (referenceId) {
    localStorage.setItem(APPLY_LOAN_AADHAAR_REF_KEY, String(referenceId));
  }
};

export const getApplyLoanAadhaarOtpDraft = () => {
  try {
    const raw = localStorage.getItem(APPLY_LOAN_AADHAAR_OTP_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const aadhaar = String(parsed.aadhaar || "").replace(/\D/g, "").slice(0, 12);
    const sentAt = Number(parsed.sentAt) || 0;
    if (aadhaar.length !== 12 || !sentAt) return null;
    const retrySeconds =
      Number(parsed.retrySeconds) || AADHAAR_OTP_RESEND_SECONDS;
    const elapsedSec = Math.floor((Date.now() - sentAt) / 1000);
    const retryTimer = Math.max(0, retrySeconds - elapsedSec);
    return {
      aadhaar,
      referenceId: String(parsed.referenceId || ""),
      sentAt,
      retrySeconds,
      retryTimer,
      apiMessage: String(parsed.apiMessage || ""),
      responseCode: String(parsed.responseCode || ""),
      consent: parsed.consent !== false,
      otpSent:
        String(parsed.responseCode || "").toUpperCase() === "SOS174" ||
        /sent|success|generated/i.test(String(parsed.apiMessage || "")),
    };
  } catch {
    return null;
  }
};

export const clearApplyLoanAadhaarOtpDraft = () => {
  localStorage.removeItem(APPLY_LOAN_AADHAAR_OTP_DRAFT_KEY);
};

export const saveApplyLoanDigitapDraft = ({
  pan,
  transactionId,
  kycUrl,
  startedAt,
  mobileVerified,
  panVerified,
}) => {
  const txn = String(transactionId || "")
    .replace(/[{}]/g, "")
    .replace(/\D/g, "");
  if (!txn) return;
  const draft = {
    pan: String(pan || "").toUpperCase().trim(),
    transactionId: txn,
    kycUrl: String(kycUrl || "").trim(),
    startedAt: Number(startedAt) || Date.now(),
    pending: true,
    mobileVerified: mobileVerified !== false,
    panVerified: panVerified !== false,
    digitapPending: true,
    digitapTransactionId: txn,
  };
  localStorage.setItem(APPLY_LOAN_DIGITAP_DRAFT_KEY, JSON.stringify(draft));
  localStorage.setItem(APPLY_LOAN_DIGITAP_TXN_KEY, txn);
};

export const getApplyLoanDigitapDraft = () => {
  try {
    const raw = localStorage.getItem(APPLY_LOAN_DIGITAP_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const transactionId = String(
      parsed.transactionId || parsed.digitapTransactionId || ""
    )
      .replace(/[{}]/g, "")
      .replace(/\D/g, "");
    if (!transactionId) return null;
    return {
      pan: String(parsed.pan || "").toUpperCase().trim(),
      transactionId,
      kycUrl: String(parsed.kycUrl || "").trim(),
      startedAt: Number(parsed.startedAt) || 0,
      pending: parsed.pending !== false && parsed.digitapPending !== false,
      mobileVerified: parsed.mobileVerified !== false,
      panVerified: parsed.panVerified !== false,
    };
  } catch {
    return null;
  }
};

export const clearApplyLoanDigitapDraft = () => {
  localStorage.removeItem(APPLY_LOAN_DIGITAP_DRAFT_KEY);
  localStorage.removeItem(APPLY_LOAN_DIGITAP_TXN_KEY);
  localStorage.removeItem(APPLY_LOAN_DIGITAP_CALLBACK_TXN_KEY);
};

const DIGILOCKER_RETURN_PATH_KEY = "fundswalla_digilocker_return_path";

/** Page that started a same-tab DigiLocker trip (backend callback always lands on /apply-loan) */
export const saveDigilockerReturnPath = (path) => {
  try {
    if (path) localStorage.setItem(DIGILOCKER_RETURN_PATH_KEY, String(path));
  } catch {
    /* ignore */
  }
};

export const getDigilockerReturnPath = () => {
  try {
    return localStorage.getItem(DIGILOCKER_RETURN_PATH_KEY) || "";
  } catch {
    return "";
  }
};

export const clearDigilockerReturnPath = () => {
  try {
    localStorage.removeItem(DIGILOCKER_RETURN_PATH_KEY);
  } catch {
    /* ignore */
  }
};

export const saveApplyLoanDigitapCallbackTxn = (txnId) => {
  const txn = String(txnId || "")
    .replace(/[{}]/g, "")
    .replace(/\D/g, "");
  if (!txn) return;
  try {
    sessionStorage.setItem(APPLY_LOAN_DIGITAP_CALLBACK_TXN_KEY, txn);
    localStorage.setItem(APPLY_LOAN_DIGITAP_TXN_KEY, txn);
  } catch {
    /* ignore */
  }
};

export const getApplyLoanDigitapCallbackTxn = () => {
  try {
    return String(
      sessionStorage.getItem(APPLY_LOAN_DIGITAP_CALLBACK_TXN_KEY) ||
        localStorage.getItem(APPLY_LOAN_DIGITAP_TXN_KEY) ||
        ""
    )
      .replace(/[{}]/g, "")
      .replace(/\D/g, "");
  } catch {
    return "";
  }
};

export const markApplyLoanDigitapCallbackDone = (txnId) => {
  const txn = String(txnId || "")
    .replace(/[{}]/g, "")
    .replace(/\D/g, "");
  if (!txn) return;
  try {
    sessionStorage.setItem(`fundswalla_aadhaar_cb_done_${txn}`, "1");
  } catch {
    /* ignore */
  }
};

export const isApplyLoanDigitapCallbackDone = (txnId) => {
  const txn = String(txnId || "")
    .replace(/[{}]/g, "")
    .replace(/\D/g, "");
  if (!txn) return false;
  try {
    return sessionStorage.getItem(`fundswalla_aadhaar_cb_done_${txn}`) === "1";
  } catch {
    return false;
  }
};

export const markApplyLoanActive = () => {
  localStorage.setItem(APPLY_LOAN_FLAG_KEY, "1");
};

export const isApplyLoanActive = () =>
  localStorage.getItem(APPLY_LOAN_FLAG_KEY) === "1";

export const saveApplyLoanStep = (step) => {
  const n = Number(step);
  if (!Number.isFinite(n) || n < 1 || n > 4) return;
  localStorage.setItem(APPLY_LOAN_STEP_KEY, String(n));
  markApplyLoanActive();
  // Keep Aadhaar identifiers durable across refresh (apply-loan only copies)
  const aadhaar = getAadhaarNumberSecure();
  const ref = getAadhaarReferenceSecure();
  if (aadhaar) localStorage.setItem(APPLY_LOAN_AADHAAR_NUMBER_KEY, aadhaar);
  if (ref) localStorage.setItem(APPLY_LOAN_AADHAAR_REF_KEY, ref);
};

export const getApplyLoanStep = () => {
  const n = Number(localStorage.getItem(APPLY_LOAN_STEP_KEY) || "1");
  if (!Number.isFinite(n) || n < 1) return 1;
  if (n > 4) return 4;
  return n;
};

/** Restore session copies so LoanForm1 / Aadhaar helpers still work after refresh */
export const restoreApplyLoanSecureFields = () => {
  const aadhaar = localStorage.getItem(APPLY_LOAN_AADHAAR_NUMBER_KEY) || "";
  const ref = localStorage.getItem(APPLY_LOAN_AADHAAR_REF_KEY) || "";
  if (aadhaar) {
    sessionStorage.setItem("aadhaarNumber", aadhaar);
    localStorage.setItem("aadhaarNumber", aadhaar);
  }
  if (ref) {
    sessionStorage.setItem("aadhaarReferenceId", ref);
    localStorage.setItem("aadhaarReferenceId", ref);
  }
};

/** Durable Aadhaar number for apply-loan (survives back/refresh) */
export const persistApplyLoanAadhaarNumber = (aadhaar) => {
  const clean = String(aadhaar || "").replace(/\D/g, "").slice(0, 12);
  if (clean.length !== 12) return "";
  localStorage.setItem(APPLY_LOAN_AADHAAR_NUMBER_KEY, clean);
  sessionStorage.setItem("aadhaarNumber", clean);
  localStorage.setItem("aadhaarNumber", clean);
  return clean;
};

export const getApplyLoanAadhaarNumber = () => {
  restoreApplyLoanSecureFields();
  const fromApply = String(
    localStorage.getItem(APPLY_LOAN_AADHAAR_NUMBER_KEY) || ""
  ).replace(/\D/g, "");
  if (fromApply.length === 12) return fromApply.slice(0, 12);
  return String(getAadhaarNumberSecure() || "")
    .replace(/\D/g, "")
    .slice(0, 12);
};

const hasMobileDone = () => {
  const mobile = String(localStorage.getItem("mobileNumber") || "").replace(/\D/g, "");
  return localStorage.getItem("mobile_verified") === "true" && mobile.length === 10;
};

const hasAadhaarDone = () => {
  try {
    const raw = localStorage.getItem("aadhaarData");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Boolean(parsed && (parsed.name || parsed.fullName));
  } catch {
    return false;
  }
};

const hasPanDone = () => {
  try {
    if (localStorage.getItem("pan_verified") !== "true") return false;
    const raw = localStorage.getItem("loanFormData");
    if (!raw) return false;
    const loan = JSON.parse(raw);
    const pan = String(loan.pan || loan.pan_verifiedPan || "")
      .toUpperCase()
      .trim();
    return pan.length === 10;
  } catch {
    return false;
  }
};

const hasIdentityDone = () => hasAadhaarDone() && hasPanDone();

/**
 * Resume furthest valid step based on saved step + available data.
 * Steps: 1 mobile → 2 identity → 3 loan → 4 thank-you (not resumed).
 * Legacy contact step (old 3) / old loan (old 4) map into new loan (3).
 */
export const resolveApplyLoanResumeStep = () => {
  restoreApplyLoanSecureFields();

  let step = getApplyLoanStep();

  // Thank-you should not resume mid-success screen
  if (step >= 4) {
    // Legacy: step 4 was loan form before contact was removed
    if (step === 4 && hasIdentityDone()) {
      step = 3;
    } else {
      return 1;
    }
  }

  if (step >= 2 && !hasMobileDone()) step = 1;
  if (step >= 3 && !hasIdentityDone()) step = hasMobileDone() ? 2 : 1;

  return step;
};

/** Wipe apply-loan progress keys only */
export const clearApplyLoanProgressKeys = () => {
  APPLY_LOAN_ONLY_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

/** Full clear after successful submit (shared PII + apply-loan keys) */
export const clearApplyLoanStorage = () => {
  clearApplyLoanProgressKeys();
  clearApplyFlowOnSuccess();
};
